import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  Scale,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import PartyEntryDialog from "../components/PartyEntryDialog";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import api, { apiErrorText } from "../lib/api";
import {
  currentNepalMonth,
  fmtCurrency,
  fmtUSDT,
  monthAdd,
  monthLabel,
  partyEntryOriginal,
} from "../lib/partyBook";

export default function PartyBookDetails() {
  const navigate = useNavigate();
  const { partyId } = useParams();
  const [period, setPeriod] = useState("all");
  const [month, setMonth] = useState(currentNepalMonth());
  const [party, setParty] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entryOpen, setEntryOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const filterParams = useMemo(() => {
    const params = { period };
    if (period === "monthly") params.month = month;
    return params;
  }, [period, month]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboardResponse, entriesResponse] = await Promise.all([
        api.get("/party-book/dashboard", { params: filterParams }),
        api.get("/party-book/entries", { params: { ...filterParams, party_id: partyId } }),
      ]);
      setParty((dashboardResponse.data.parties || []).find((item) => item.id === partyId) || null);
      setEntries(entriesResponse.data.items || []);
    } catch (error) {
      toast.error(apiErrorText(error));
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [filterParams, partyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filterLabel = period === "all" ? "All Time" : monthLabel(month);

  const openNewEntry = () => {
    setEditingEntry(null);
    setEntryOpen(true);
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setEntryOpen(true);
  };

  const deleteEntry = async () => {
    if (!deletingEntry?.id) return;
    setDeleting(true);
    try {
      await api.delete(`/party-book/entries/${deletingEntry.id}`);
      toast.success("Entry deleted");
      setDeletingEntry(null);
      await loadData();
    } catch (error) {
      toast.error(apiErrorText(error));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout
      title={party?.name || (loading ? "Party Ledger" : "Party not found")}
      subtitle={party ? `${party.contact || "No contact"} · Full party statement · Nepal time` : "Party Book"}
      testId="party-book-details-page"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/party-book")} className="h-9">
            <ArrowLeft size={14} className="mr-1.5" /> Party Book
          </Button>
          <Button onClick={openNewEntry} disabled={!party} className="h-9 bg-zinc-900 text-white hover:bg-zinc-800">
            <Plus size={14} className="mr-1.5" /> Add entry
          </Button>
        </div>
      }
    >
      <div className="stat-card mb-5 flex flex-wrap items-center gap-2 p-3">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="h-8 w-[150px]" data-testid="party-ledger-period-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
          </SelectContent>
        </Select>
        {period === "monthly" && (
          <>
            <button onClick={() => setMonth(monthAdd(month, -1))} className="flex h-8 w-8 items-center justify-center rounded-md border hairline bg-white hover:bg-zinc-50" aria-label="Previous month">
              <ChevronLeft size={14} strokeWidth={1.7} />
            </button>
            <div className="min-w-[130px] px-2 text-center font-display text-[14px] font-semibold">{monthLabel(month)}</div>
            <button onClick={() => setMonth(monthAdd(month, 1))} className="flex h-8 w-8 items-center justify-center rounded-md border hairline bg-white hover:bg-zinc-50" aria-label="Next month">
              <ChevronRight size={14} strokeWidth={1.7} />
            </button>
            {month !== currentNepalMonth() && (
              <button onClick={() => setMonth(currentNepalMonth())} className="h-8 rounded-md border hairline bg-white px-3 text-[12px] hover:bg-zinc-50">This month</button>
            )}
          </>
        )}
        <div className="ml-auto flex items-center gap-1.5 text-[11.5px] text-zinc-500">
          <CalendarDays size={13} /> Asia/Kathmandu
        </div>
      </div>

      {party?.notes && (
        <div className="stat-card mb-5 px-4 py-3 text-[12.5px] text-zinc-600">
          <span className="mr-2 font-medium text-zinc-900">Party note:</span>{party.notes}
        </div>
      )}

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <TotalCard label="Received" value={party?.total_received} icon={ArrowDownLeft} tone="emerald" footer={filterLabel} />
        <TotalCard label="Sent" value={party?.total_sent} icon={ArrowUpRight} tone="rose" footer={filterLabel} />
        <TotalCard label="Net" value={party?.net_usdt} icon={Scale} tone="sky" footer={`Received − Sent · ${filterLabel}`} />
      </div>

      <section className="stat-card overflow-hidden" data-testid="party-ledger-table">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b hairline px-5 py-4">
          <div>
            <h2 className="font-display text-[16px] font-semibold text-zinc-900">Ledger entries</h2>
            <p className="mt-0.5 text-[12px] text-zinc-500">Every sent and received entry for {filterLabel.toLowerCase()}</p>
          </div>
          <div className="font-mono text-[12px] text-zinc-500">{entries.length} entries</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[12.5px]">
            <thead>
              <tr className="border-b hairline bg-zinc-50/50 text-left text-[10.5px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 text-right font-medium">Original amount</th>
                <th className="px-4 py-3 text-right font-medium">Rate</th>
                <th className="px-4 py-3 text-right font-medium">USDT</th>
                <th className="px-4 py-3 font-medium">Reference</th>
                <th className="px-4 py-3 font-medium">Notes</th>
                <th className="px-4 py-3 font-medium">Added by</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-zinc-500">Loading ledger...</td></tr>
              ) : !party ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-zinc-500">Party not found.</td></tr>
              ) : entries.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-zinc-500">No entries for {filterLabel.toLowerCase()}.</td></tr>
              ) : entries.map((entry) => {
                const original = partyEntryOriginal(entry);
                return (
                  <tr key={entry.id} className="row-hover">
                    <td className="whitespace-nowrap px-4 py-3 font-mono">{entry.date}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded border px-1.5 py-0.5 text-[10.5px] ${entry.direction === "received" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
                        {entry.direction === "received" ? "Received" : "Sent"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-mono">{fmtCurrency(original.amount, original.currency)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-zinc-600">
                      {original.currency === "USD"
                        ? `${Number(original.rate).toLocaleString("en-US", { maximumFractionDigits: 6 })}%`
                        : original.currency === "NPR"
                          ? `${Number(original.rate).toLocaleString("en-US", { maximumFractionDigits: 6 })} NPR`
                          : "1"}
                    </td>
                    <td className={`whitespace-nowrap px-4 py-3 text-right font-mono ${entry.direction === "received" ? "text-emerald-700" : "text-rose-700"}`}>{fmtUSDT(entry.amount_usdt)}</td>
                    <td className="max-w-[180px] px-4 py-3">{entry.reference || "-"}</td>
                    <td className="max-w-[260px] whitespace-pre-wrap px-4 py-3 text-zinc-600">{entry.notes || "-"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-zinc-500">{entry.created_by_name || "-"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEditEntry(entry)} className="rounded p-1.5 text-zinc-600 hover:bg-zinc-100" title="Edit entry" aria-label="Edit entry">
                          <Pencil size={14} strokeWidth={1.7} />
                        </button>
                        <button onClick={() => setDeletingEntry(entry)} className="rounded p-1.5 text-rose-600 hover:bg-rose-50" title="Delete entry" aria-label="Delete entry">
                          <Trash2 size={14} strokeWidth={1.7} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <PartyEntryDialog
        open={entryOpen}
        onOpenChange={setEntryOpen}
        parties={party ? [party] : []}
        initialPartyId={partyId}
        entry={editingEntry}
        onSaved={loadData}
      />

      <AlertDialog open={Boolean(deletingEntry)} onOpenChange={(open) => !open && !deleting && setDeletingEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this ledger entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the {deletingEntry?.direction || "selected"} entry of {fmtUSDT(deletingEntry?.amount_usdt)} and recalculates party totals. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteEntry} disabled={deleting} className="bg-rose-600 text-white hover:bg-rose-700">
              {deleting ? "Deleting..." : "Delete entry"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function TotalCard({ label, value, icon: Icon, tone, footer }) {
  const tones = {
    rose: { icon: "bg-rose-50 text-rose-600", value: "text-rose-700" },
    emerald: { icon: "bg-emerald-50 text-emerald-600", value: "text-emerald-700" },
    sky: { icon: "bg-sky-50 text-sky-600", value: Number(value) < 0 ? "text-rose-700" : "text-zinc-900" },
  };
  const selected = tones[tone] || tones.sky;
  return (
    <div className="stat-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] uppercase tracking-widest text-zinc-500">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-md ${selected.icon}`}><Icon size={16} strokeWidth={1.7} /></span>
      </div>
      <div className={`mt-3 font-mono text-[23px] font-medium ${selected.value}`}>{fmtUSDT(value)}</div>
      <div className="mt-1.5 text-[10.5px] text-zinc-400">{footer}</div>
    </div>
  );
}
